/**
 * Данные беты (`mode: "beta"`): слоты базы, кристаллы, магазин, колесо, сундуки, осколки, описания героев.
 * Лежат в `packages/engine/data/beta/beta.json` и существуют только в балансе беты; обычный режим раздела не имеет.
 */
import { ContentId, HeroId, PosInt, UInt } from "../common";
import { z } from "zod";

/** Тип сундука. */
export const ChestType = z.enum(["wood", "silver", "gold"]);
export type ChestType = z.infer<typeof ChestType>;

/** Награда сундука или сектора колеса. `coinWaves` — столько наград волны (на лучшей волне игрока) монетами. */
export const BetaReward = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("coins"), coinWaves: PosInt }),
  z.object({ kind: z.literal("gems"), gems: PosInt }),
  /** Осколки случайного героя: из открытых (`premium: false`) или из премиум-героев (`premium: true`). */
  z.object({ kind: z.literal("shards"), shards: PosInt, premium: z.boolean().default(false) }),
]);
export type BetaReward = z.infer<typeof BetaReward>;

export const WheelSector = z.object({
  id: ContentId,
  weight: PosInt,
  reward: z.union([
    BetaReward,
    z.object({ kind: z.literal("boost"), minutes: PosInt, mulPermille: PosInt }),
    z.object({ kind: z.literal("chest"), chest: ChestType }),
  ]),
});
export type WheelSector = z.infer<typeof WheelSector>;

/**
 * Режим «Карта» (захват участков): регионы, энергия, щит, доход, бой атаки и сила защиты участков.
 * Лежит в `beta.json` → `map`.
 */
export const MapDef = z.object({
  /** Игроков в регионе (при достижении создаётся новый) и сторона сетки участков. */
  region: z.object({ capacity: PosInt, grid: PosInt, tierSpreadWaves: PosInt }),
  /** Энергия атак: максимум, час на единицу (мс), цена одной единицы в кристаллах. */
  energy: z.object({ max: PosInt, regenMs: PosInt, gemsPerUnit: PosInt }),
  /** Щит после захвата, мс. */
  shieldMs: PosInt,
  /** Потолок накопления дохода, часов. */
  incomeCapHours: PosInt,
  /** Атака участка: время, подготовка, прочность платформ атакующего, урон героев-защитников по платформам. */
  attack: z.object({
    timeLimitTicks: PosInt,
    introTicks: UInt,
    platformHpPermille: PosInt,
    defenderDamagePermille: PosInt,
    spawnRadius: PosInt,
    /** Доля награды за бой (монеты участка за час × `partialHours`), если участок перехватили раньше, промилле. */
    partialPermille: z.number().int().min(0).max(1000),
  }),
  /**
   * Сила защиты свободного участка по уровню (1…N от края к центру): какую долю волны региона берут зомби и база,
   * база — в долях прочности `baseHpWaves`-волны, размер и темп волн защитников, босс.
   */
  levels: z
    .array(
      z.object({
        wavePermille: PosInt,
        baseHpPermille: PosInt,
        waveSize: PosInt,
        waveGapTicks: PosInt,
        boss: z.boolean(),
        /** Монеты в час: столько наград волны региона. */
        coinWaves: PosInt,
        /** Кристаллов в час (0 — нет) и осколков в час (0 — нет) для «ценных» участков. */
        gemsPerHour: UInt,
        shardsPerHour: UInt,
        /** Очки рейтинга региона за участок. */
        rating: PosInt,
      }),
    )
    .min(1),
  /**
   * Прочность базы: опорный урон героев за бой `baseHpRef · волна^(baseHpExpPermille/1000)` (снято симулятором баланса
   * `sim-bot/map.ts`), дальше доля уровня участка (`levels[].baseHpPermille`).
   */
  baseHpRef: PosInt,
  baseHpExpPermille: PosInt,
  /** Защита чужого участка (копии героев): прочность базы = опорный урон по волне владельца × это, промилле. */
  pvpBaseHpPermille: PosInt,
  firstCaptureChest: ChestType,
});
export type MapDef = z.infer<typeof MapDef>;

export const BetaDef = z.object({
  map: MapDef,
  /** Слоты базы: `start` + 1 за каждые `everyBaseLevels` уровней ветки Tech `base`, не больше `max`. */
  slots: z.object({ start: PosInt, everyBaseLevels: PosInt, max: PosInt.max(16), copiesMax: PosInt }),
  baseTechId: ContentId,
  /** Кристаллы из игры. */
  gems: z.object({ bossLevel: UInt, streakDay7: UInt }),
  /** Пакеты кристаллов за Telegram Stars (`XTR`). */
  packs: z.array(z.object({ id: ContentId, gems: PosInt, stars: PosInt })).min(1),
  shop: z.object({
    coinPacks: z.array(z.object({ id: ContentId, gems: PosInt, coinWaves: PosInt })).length(3),
    /** Премиум-герой: открывается за кристаллы (`gems`) или сам при уровне ветки Tech `base` не ниже `baseLevel` (одно из двух). */
    premiumHeroes: z
      .array(
        z
          .object({ heroId: HeroId, gems: PosInt.optional(), baseLevel: PosInt.optional() })
          .refine((h) => (h.gems === undefined) !== (h.baseLevel === undefined), "gems xor baseLevel"),
      )
      .min(1),
    spin: z.object({ gems: PosInt }),
    chests: z.array(z.object({ chest: ChestType, gems: PosInt })),
  }),
  wheel: z.object({
    freeCooldownMs: PosInt,
    sectors: z.array(WheelSector).length(8),
  }),
  chests: z.object({
    inventoryLimit: PosInt,
    woodEveryLevels: PosInt,
    bossLevelChest: ChestType,
    /** Сколько раз кидается таблица на сундук. */
    rolls: z.record(ChestType, PosInt),
    table: z.record(ChestType, z.array(z.object({ weight: PosInt, reward: BetaReward })).min(1)),
  }),
  /** Звезда героя за осколки: звезда k → `shards[k−1]` осколков и `coinWaves[k−1]` наград волны монетами. */
  stars: z.object({ shards: z.array(PosInt).length(5), coinWaves: z.array(UInt).length(5) }),
  /** Короткие описания героев карточки (EN/RU). */
  heroTexts: z.record(HeroId, z.object({ en: z.string().min(1), ru: z.string().min(1) })),
});
export type BetaDef = z.infer<typeof BetaDef>;
